import Spacer from '@berlitz/spacer'
import MyBerlitzLesson from '@classes/Lesson'
import PortalPrograms from '@classes/Programs'
import AddNoteModal from '@components/AddNoteModal'
import BlockSchedulingModal from '@components/BlockScheduling/modal'
import Fileupload from '@components/Fileupload'
import InvisibleText from '@components/InvisibleText'
import JoinLessonButton from '@components/JoinLessonButton'
import { LessonTypeIcon } from '@components/ListCard'
import { BasicModal, LoadingModal, WarningModal } from '@components/Modal'
import usePortalFeatures from '@components/RoleBasedView/usePortalFeatures'
import SelfTechCheck from '@components/SelfTechCheck'
import WithUserTimeHOC from '@hocs/WithUserTime'
import { Attachment as IAttachment } from '@interfaces/ScheduleSession'
import { StoreProps } from '@interfaces/StoreState'
import CardActions from '@layouts/Scheduling/ScheduleListCard/CardActions'
import ClassDetails from '@layouts/Scheduling/ScheduleListCard/ClassDetails'
import { DATE_FORMAT } from '@utils/constants'
import moment from 'moment'
import Link from 'next/link'
import Router from 'next/router'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Col, Row } from 'react-styled-flexboxgrid'
import CancelLessonModal from './CancelLessonModal'
import { IScheduleListCardProps } from './interface'
import LessonStatus from './LessonStatus'
import RebookInstructorModal from './RebookInstructor'
import RescheduleWarningModal from './RescheduleWarningModal'
import {
  Breaker,
  CardBox,
  CardIconCol,
  ContentCol,
  DateDiv,
  DisabledLink,
  GroupBreaker,
  GroupButtonsWrapper,
  ProgramDiv,
  SelfTechCheckWrapper,
  StyledButton,
  StyledGrid,
} from './style'
import useTimeFormat from '@hooks/useTimeFormat'

const CANCEL_LESSON_TEXT = 'Cancelling lesson'
const UPDATE_NOTE_TEXT = 'Updating note'
const UPDATE_NOTIFTY = 'Notifying in progress'
const CANCELLED_LESSON = 'Your lesson has been cancelled'
const SAVED_NOTIFY = 'Your absence has been recorded'
const CONFIRM_ABSENCE_NOTIFY = 'Your absence has been confirmed'

const ScheduleListCard: React.FC<IScheduleListCardProps> = ({
  lesson,
  intl,
  putSchedules,
  onCancelLesson,
  isFirst,
  isGroup,
  isLast,
  toUserDate,
  sessionStartTime,
  refetch,
  isFeedbackEnabled,
  getUserDate,
  getDateWithUserTimezone,
}) => {
  const { state } = usePortalFeatures({
    configId: 'page::scheduling::instructors',
  })
  const { convertToUserTimeFormats } = useTimeFormat()

  const { isStudent, StudentProgram, profile } = useSelector(({ userProfile }: StoreProps) => ({
    isStudent: userProfile?.info?.Type === 'Student',
    StudentProgram: new PortalPrograms(
      userProfile?.info?.StudentProfile?.StudentProfileInfo?.ProgramStudentWrappers ?? []
    ).getProgram(lesson?.ProgramId),
    profile: userProfile?.info,
  }))
  const StudentLesson = new MyBerlitzLesson(lesson, StudentProgram)

  const [openUploadFilesDialog, setOpenUploadFilesDialog] = useState(false)
  const [openAddNoteModal, setOpenAddNoteModal] = useState(false)
  const [modalWarningOpen, setModalWarningOpen] = useState(false)
  const [modalRescheduleWarningOpen, setModalRescheduleWarningOpen] = useState(false)
  const [modalReschedInstructorOpen, setModalReschedInstructorOpen] = useState(false)
  const [modalBlockSchedulingOpen, setModalBlockSchedulingOpen] = useState(false)
  const [modalLoadingOpen, setModalLoadingOpen] = useState(false)
  const [modalNotifyOpen, setModalNotifyOpen] = useState(false)
  const [modalNotifyAbsenceOpen, setModalNotifyAbsenceOpen] = useState(false)
  const [modalConfirmedAbsenceOpen, setModalConfirmedAbsenceOpen] = useState(false)
  const [modalErrorOpen, setModalErrorOpen] = useState(false)
  const [isNoteLoading, setIsNoteLoading] = useState(false)
  const [modalSuccessOpen, setModalSuccessOpen] = useState(false)
  const [hasCancelError, setHasCancelError] = useState(false)
  const [isProcessingSave, setIsProcessingSave] = useState(true)
  const [modalLoadingText, setModalLoadingText] = useState(CANCEL_LESSON_TEXT)
  const [modalSuccessText, setModalSuccessText] = useState(CANCELLED_LESSON)
  const [parentLoading, setParentLoading] = useState(false)
  const [parentSuccess, setParentSuccess] = useState(false)
  const [parentLoadingTextSuccess, setParentLoadingTextSuccess] = useState('')
  const [parentLoadingText, setParentLoadingText] = useState('')
  const [outdatedSessionModal, setOutdatedSessionModal] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [attachments, setAttachments] = useState<IAttachment[]>(lesson?.Attachments ?? [])

  useEffect(() => {
    setAttachments(lesson?.Attachments ?? [])
  }, [lesson?.Attachments])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!outdatedSessionModal && mounted) {
      window.scroll(0, 0)
      refetch()
    }
  }, [outdatedSessionModal])

  const closeFilesDialog = () => setOpenUploadFilesDialog(false)
  const closeModalWarning = () => setModalWarningOpen(false)
  const closeModalRescheduleWarning = () => setModalRescheduleWarningOpen(false)
  const closeModalBlockScheduling = () => setModalBlockSchedulingOpen(false)
  const closeModalLoading = () => setModalLoadingOpen(false)
  const closeModalNotify = () => setModalNotifyOpen(false)
  const closeModalNotifyAbsence = () => setModalNotifyAbsenceOpen(false)
  const closeModalConfirmedAbsence = () => setModalConfirmedAbsenceOpen(false)
  const closeModalError = () => setModalErrorOpen(false)
  const openFilesDialog = () => setOpenUploadFilesDialog(true)
  const openModalNotify = () => setModalNotifyOpen(true)
  const openModalNotifyAbsence = () => setModalNotifyAbsenceOpen(true)
  const openModalConfirmedAbsence = () => setModalConfirmedAbsenceOpen(true)
  const toggleAddNoteModal = () => {
    setOpenAddNoteModal(!openAddNoteModal)
    setIsProcessingSave(true)
  }
  const closeModalSuccess = () => {
    setModalSuccessOpen(false)
    setModalSuccessText(CANCELLED_LESSON)
  }
  const toggleReschedInstructor = () =>
    StudentProgram?._rescheduleCount < 1 ? setModalRescheduleWarningOpen(true) : setModalReschedInstructorOpen(true)
  const cancelCurrentLesson = () =>
    !StudentLesson?.afterCutoffTime(sessionStartTime, StudentProgram?.program?.CancelCutOffMinutes) ||
    StudentProgram.isEnrollDateAfterUserDate(sessionStartTime)
      ? setModalWarningOpen(true)
      : setOutdatedSessionModal(true)
  const rescheduleCurrentLesson = () =>
    StudentLesson?.afterCutoffTime(sessionStartTime, StudentProgram?.program?.SchedulingMinutesBeforeCutoff)
      ? setOutdatedSessionModal(true)
      : StudentProgram.isFlexProgram() || !state.allowed
        ? setModalRescheduleWarningOpen(true)
        : toggleReschedInstructor()

  const isPastLesson = StudentLesson?.isPastLesson()
  const showRescheduleButton = StudentLesson?.showRescheduleButton(sessionStartTime)
  const showReschedBlockModal = StudentLesson?.showReschedBlockModal()
  const SchedulingBeforeCutoffTime = StudentProgram?.getCutOffTime(
    profile?.Timezone,
    StudentProgram?.program?.SchedulingMinutesBeforeCutoff
  )
  const CancelCutoffTime = StudentProgram?.getCutOffTime(
    profile?.Timezone,
    StudentProgram?.program?.CancelCutOffMinutes
  )
  // Apply same condition as Join online button for showing CardActions
  // hide for F2F programs also
  const provider = (lesson?.VirtualClassRoomProvider ?? '').toLowerCase()
  const endTime = getDateWithUserTimezone(lesson?.EndTime)
  const shouldShowCardActions =
    StudentProgram?._showLessonActions &&
    !!provider &&
    !getUserDate().isAfter(endTime) &&
    !StudentProgram?.isProgramF2FStar()

  const onSave = (files) => {
    setParentLoading(true)
    setParentSuccess(false)
    setParentLoadingText('Attaching files')

    const data = [
      {
        Id: lesson.Id,
        StudentNote: lesson?.StudentNote,
        Attachments: files.map((file) => ({
          FileUrl: file.url,
          Title: file.name,
          Status: 'Created',
          SessionScheduleId: lesson.Id,
        })),
      },
    ]
    putSchedules({ variables: { SessionInfo: data } }).then(() => {
      setParentLoading(false)
      setParentSuccess(true)
      setParentLoadingTextSuccess('Successfully attached files')
    })
  }

  const onNoteSave = (note) => {
    setIsNoteLoading(true)

    const data = [
      {
        Id: lesson.Id,
        StudentNote: note,
        // api is generating error if attachments is not passed
        Attachments: [],
      },
    ]
    putSchedules({ variables: { SessionInfo: data } }).then(() => {
      setIsProcessingSave(false)
      setIsNoteLoading(false)
    })
  }

  const onRaiseACase = () =>
    Router.push(`/support/contact-us/?pid=${StudentProgram.ProgramId}&id=my-lessons&cat=Modify%20schedule`)

  const onNotifySave = (successMessage?: string, isSessionLevel?: boolean) => {
    setModalWarningOpen(false)
    setModalNotifyOpen(false)
    setModalNotifyAbsenceOpen(false)
    setModalLoadingOpen(true)
    setModalLoadingText(UPDATE_NOTIFTY)
    const data = [
      {
        Id: lesson.Id,
        Attendance: 'Absent',
        AbsenceType: isSessionLevel ? 'All classes' : 'This class',
        // api is generating error if attachments is not passed
        Attachments: [],
      },
    ]
    putSchedules({ variables: { SessionInfo: data } }).then(
      () => {
        setModalWarningOpen(false)
        setModalLoadingOpen(false)
        setModalLoadingText(CANCEL_LESSON_TEXT)
        setModalSuccessOpen(true)
        setModalSuccessText(successMessage || SAVED_NOTIFY)
      },
      () => {
        setModalErrorOpen(true)
        setModalLoadingOpen(false)
        setModalLoadingText(CANCEL_LESSON_TEXT)
        setModalSuccessText(CANCELLED_LESSON)
      }
    )
  }

  const onDelete = (file: IAttachment) => {
    setParentLoading(true)
    setParentSuccess(false)
    setParentLoadingText('Deleting file')
    const data = [
      {
        Id: lesson.Id,
        StudentNote: lesson?.StudentNote,
        Attachments: [
          {
            RecordId: file.RecordId,
            Status: 'Deleted',
            SessionScheduleId: lesson.Id,
          },
        ],
      },
    ]
    putSchedules({ variables: { SessionInfo: data } }).then(() => {
      setParentLoading(false)
      setParentSuccess(true)
      setParentLoadingText('Successfully deleted file')
      setAttachments((prev) => prev.filter((f) => f.RecordId !== file.RecordId))
    })
  }

  const cancelLesson = ({ data, loading, error }) => {
    const isSuccess = data?.cancelSessions?.ServiceResult?.Status === 'Success'
    const hasError = !loading && error
    setModalWarningOpen(hasError)
    setModalLoadingOpen(loading)
    setHasCancelError(hasError)

    isSuccess && onCancelLesson(lesson?.SessionId)
  }

  const generateRescheduleButton = () => {
    const WrapperComponent = StyledButton

    return showRescheduleButton ? (
      <WrapperComponent
        onClick={showReschedBlockModal ? () => setModalBlockSchedulingOpen(true) : rescheduleCurrentLesson}
      >
        <FormattedMessage id="Re-schedule" />
      </WrapperComponent>
    ) : null
  }

  const generateNotifyAbsenceButton = () =>
    StudentLesson?.isNotifyAbsenceAllowed(sessionStartTime) ? (
      (lesson.Attendance || '').toLocaleLowerCase() === 'absent' ? (
        <a className="link-btn" onClick={openModalConfirmedAbsence}>
          <FormattedMessage id="Notify of absence - confirmed" />
        </a>
      ) : (
        <a className="link-btn" onClick={openModalNotifyAbsence}>
          <FormattedMessage id="Notify of absence" />
        </a>
      )
    ) : null

  const generateCancelButton = () =>
    StudentLesson?.isCancellable(sessionStartTime) ? (
      <a className="link-btn" onClick={cancelCurrentLesson}>
        <FormattedMessage id="cancel-lesson-button" defaultMessage="Cancel" />
      </a>
    ) : null

  const generateNotifyButton = () =>
    StudentLesson?.isNotifiable(sessionStartTime) ? (
      <a className="link-btn" onClick={openModalNotify}>
        <FormattedMessage id="Notify of absence" />
      </a>
    ) : null

  const generateFeedbackButton = () =>
    // Hide feedback for F2F programs
    StudentProgram?.isProgramF2FStar() ? null : isFeedbackEnabled ? (
      <Link href={`/survey?date=${lesson.StartTime}`} className="link-btn">
        <FormattedMessage id="Feedback" />
      </Link>
    ) : lesson.FeedbackId ? (
      <DisabledLink className="link-btn">
        <FormattedMessage id="Feedback submitted" />
      </DisabledLink>
    ) : null

  const generateTechCheck = () => (
    <SelfTechCheck
      ProgramId={lesson?.ProgramId}
      Wrapper={SelfTechCheckWrapper}
      DeliveryMode={lesson?.DeliveryMode}
      Type={lesson?.Type}
      EndTime={lesson?.EndTime}
      VirtualClassRoomProvider={lesson?.VirtualClassRoomProvider}
    />
  )

  const ActionComponent = (
    <CardActions
      hasAttachment={!!lesson?.Attachments?.length}
      hasNote={!!lesson?.StudentNote}
      attachmentAction={openFilesDialog}
      noteAction={toggleAddNoteModal}
      pastDate={isPastLesson}
      status={lesson?.Status ?? ''}
    />
  )

  return (
    <>
      {isGroup && isLast && (
        <GroupButtonsWrapper>
          {generateNotifyAbsenceButton()}
          {generateCancelButton()}
          {generateRescheduleButton()}
          <GroupBreaker />
          <JoinLessonButton lesson={{ ...lesson }} startTimeOverride={sessionStartTime} />
        </GroupButtonsWrapper>
      )}
      <CardBox
        pastDate={isPastLesson}
        isGroup={isGroup}
        isFirst={isFirst}
        isLast={isLast}
        data-qa_sid={lesson?.SessionId}
        data-qa_lid={lesson.Id}
      >
        <StyledGrid fluid>
          <Row middle="xs">
            <CardIconCol xs={12} md={2} lg={2}>
              <Row className="cardicon-row" center="xs" middle="xs" between="xs">
                <Col xs={12}>
                  <LessonTypeIcon
                    typeName={
                      StudentProgram.isBerlitzOnDemand1()
                        ? StudentLesson.BoD1LessonName
                        : !StudentProgram.isFlexProgram()
                          ? StudentProgram.ProgramTypeDisplay
                          : StudentLesson.LCSTitle || StudentProgram.ProgramTypeDisplay
                    }
                    darkIcon={isPastLesson}
                  />
                </Col>
                <Col className="text-right" xs={5} md={false}>
                  <LessonStatus status={lesson?.Status} pastDate={isPastLesson} />
                </Col>
              </Row>
            </CardIconCol>
            <Col className="action-btns" xs={12} md={false}>
              <Row between="xs">
                {shouldShowCardActions ? (
                  <Col
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: 0,
                    }}
                  >
                    {ActionComponent}
                  </Col>
                ) : null}

                <Col
                  style={{
                    textAlign: 'right',
                    paddingLeft: 0,
                  }}
                >
                  {!isGroup && (
                    <>
                      {generateTechCheck()}
                      <JoinLessonButton lesson={{ ...lesson }} />
                      {StudentLesson?._isProgramOnline && !!showRescheduleButton && <Breaker />}
                      {generateRescheduleButton()}
                      {generateCancelButton()}
                    </>
                  )}
                  {generateFeedbackButton()}
                  {!isGroup && generateNotifyAbsenceButton()}
                </Col>
              </Row>
            </Col>
            <ContentCol xs={12} md={10} lg={10}>
              <Row between="md">
                {!StudentLesson._isMeeting && (
                  <Col xs={12} md={6} lg={7}>
                    <ProgramDiv>{lesson?.ProgramName}</ProgramDiv>
                  </Col>
                )}
                <Col className="text-right" xs={false} md={6} lg={5}>
                  {shouldShowCardActions && ActionComponent}
                  <LessonStatus status={lesson?.Status} pastDate={isPastLesson} />
                </Col>
              </Row>

              <Row between="md" style={{ marginTop: 4 }}>
                <Col xs={12} md={4} lg={4}>
                  <DateDiv>{StudentLesson.getUserStartTime(toUserDate, DATE_FORMAT.DEFAULT)}</DateDiv>
                  <div style={{ lineHeight: 1.3 }}>
                    {convertToUserTimeFormats(
                      StudentLesson?.getStartEndTime(toUserDate),
                      [DATE_FORMAT.HM, DATE_FORMAT.HM],
                      [DATE_FORMAT.hmm, 'default']
                    ).join(' - ')}
                  </div>
                </Col>
              </Row>
              <Row between="md" bottom="md">
                <ClassDetails
                  isOnline={StudentLesson?._isProgramOnline}
                  room={lesson.Room}
                  lc={lesson.LearningCenter}
                  lcId={lesson.LearningCenterId}
                  isMeeting={StudentLesson._isMeeting}
                  instructor={lesson.InstructorName}
                  instructorId={lesson.InstructorId}
                  ProgramName={lesson?.ProgramName}
                  ForCounselling={lesson?.ForCounselling}
                  ProgramType={StudentProgram.ProgramType}
                  isStudent={isStudent}
                  isF2fStar={StudentProgram.isProgramF2FStar()}
                  isSelfStudy={lesson.isSelfStudy}
                />
                <Col className="text-right" xs={false} md={4}>
                  <InvisibleText>
                    Id: {lesson?.SessionId} <br />
                    Schedule Cutoff Minutes: {StudentProgram?.program?.SchedulingMinutesBeforeCutoff} <br />
                    Cancel Cutoff Minutes: {StudentProgram?.program?.CancelCutOffMinutes} <br />
                    Schedule Cutoff Time: {SchedulingBeforeCutoffTime.format()} <br />
                    Session in Schedule Cutoff TZ:{' '}
                    {moment
                      .tz(moment(lesson?.StartTime, DATE_FORMAT.TZ), SchedulingBeforeCutoffTime.tz())
                      .format()}{' '}
                    <br />
                    Cancel Cutoff Time: {CancelCutoffTime.format()} <br />
                    Session in Cancel Cutoff TZ:{' '}
                    {moment.tz(moment(lesson?.StartTime, DATE_FORMAT.TZ), CancelCutoffTime.tz()).format()} <br />
                  </InvisibleText>
                  {!isPastLesson && !isGroup && (
                    <>
                      {generateTechCheck()}
                      <JoinLessonButton lesson={{ ...lesson }} />
                      {StudentLesson?._isProgramOnline && !!showRescheduleButton && <Spacer size="xs" />}
                      {generateRescheduleButton()}
                      {generateCancelButton()}
                    </>
                  )}
                  {generateFeedbackButton()}
                  {!isGroup && generateNotifyAbsenceButton()}
                </Col>
              </Row>
            </ContentCol>
          </Row>
        </StyledGrid>
        <RescheduleWarningModal
          open={modalRescheduleWarningOpen}
          onClose={closeModalRescheduleWarning}
          onProceed={() => {
            closeModalRescheduleWarning()
            Router.push(`/reschedule?programid=${lesson?.ProgramId}&sessionid=${lesson?.SessionId}`)
          }}
          rescheduleCount={StudentProgram?._rescheduleCount}
          programId={lesson?.ProgramId}
        />
        <BlockSchedulingModal
          open={modalBlockSchedulingOpen}
          onClose={closeModalBlockScheduling}
          onProceed={() => {
            Router.push('/support/contact-us/?id=my-lessons&cat=Modify%20schedule')
            closeModalBlockScheduling()
          }}
        />
        <CancelLessonModal
          open={modalWarningOpen}
          onClose={closeModalWarning}
          onProceed={cancelLesson}
          SFId={profile.SFId}
          SessionId={lesson?.SessionId}
          hasCancelError={hasCancelError}
          cancelsRemaining={StudentProgram._cancelsRemaining}
        />
        <WarningModal
          open={modalNotifyOpen}
          onClose={closeModalNotify}
          title={intl.formatMessage({
            id: 'Notify absence',
          })}
          subtitle={intl.formatMessage({
            id: 'Are you sure you want to notify of absence?',
          })}
          onCancel={onNotifySave}
          cancelLabel={intl.formatMessage({
            id: 'Yes',
          })}
          onProceed={closeModalNotify}
          proceedLabel={intl.formatMessage({
            id: 'No',
          })}
        />
        <WarningModal
          open={modalNotifyAbsenceOpen}
          onClose={closeModalNotifyAbsence}
          title={intl.formatMessage({
            id: 'Notify of absence',
          })}
          subtitle={intl.formatMessage({
            id: 'Are you sure you want to notify Berlitz of your absence for this lesson?',
          })}
          onCancel={closeModalNotifyAbsence}
          cancelLabel={intl.formatMessage({
            id: 'Keep lesson',
          })}
          onProceed={() => onNotifySave(CONFIRM_ABSENCE_NOTIFY, true)}
          proceedLabel={intl.formatMessage({
            id: 'Notify of absence',
          })}
        />
        <WarningModal
          open={modalConfirmedAbsenceOpen}
          onClose={closeModalConfirmedAbsence}
          title={intl.formatMessage({
            id: 'Notify of absence',
          })}
          subtitle={intl.formatMessage({
            id: 'You have confirmed your absence for this lesson. If you would like to change this please raise a case or contact your learning center.',
          })}
          onCancel={closeModalConfirmedAbsence}
          cancelLabel={intl.formatMessage({
            id: 'Cancel',
          })}
          onProceed={onRaiseACase}
          proceedLabel={intl.formatMessage({
            id: 'Raise a case',
          })}
        />
        <WarningModal open={modalErrorOpen} onClose={closeModalError} anApiError noButtons />
        <LoadingModal
          open={modalLoadingOpen}
          onClose={closeModalLoading}
          title={`${intl.formatMessage({
            id: modalLoadingText,
          })}...`}
          subtitle={intl.formatMessage({
            id: 'Please wait a moment.',
          })}
        />
        <BasicModal
          open={modalSuccessOpen}
          onClose={closeModalSuccess}
          type="success"
          modalTitle={intl.formatMessage({ id: modalSuccessText })}
        />
        <Fileupload
          open={openUploadFilesDialog}
          onSave={onSave}
          onDelete={onDelete}
          onClose={closeFilesDialog}
          center
          parentLoading={parentLoading}
          parentSuccess={parentSuccess}
          parentLoadingText={parentLoadingText}
          parentLoadingTextSuccess={parentLoadingTextSuccess}
          files={attachments}
          hidden={isPastLesson}
        />

        <AddNoteModal
          open={openAddNoteModal}
          onClose={toggleAddNoteModal}
          initialValues={{
            note: lesson?.StudentNote ?? '',
          }}
          onSave={onNoteSave}
          isProcessingSave={isProcessingSave}
          isLoading={isNoteLoading}
          isLoadingText={UPDATE_NOTE_TEXT}
          isNotEditable={isPastLesson}
        />
        <WarningModal
          open={outdatedSessionModal}
          onClose={() => {
            setOutdatedSessionModal(false)
          }}
          title={intl.formatMessage({
            id: 'Sorry, there was a problem with your request',
          })}
          subtitle={intl.formatMessage({
            id: 'To refresh your lessons please select Close and try again.',
          })}
          cancelLabel="Close"
        />
        <RebookInstructorModal
          open={modalReschedInstructorOpen}
          onClose={() => {
            setModalReschedInstructorOpen(false)
          }}
          StudentId={profile.SFId}
          StudentProgram={StudentProgram}
          InstructorId={StudentLesson.InstructorId}
          SessionInfo={lesson}
          onFetchError={() => {
            setModalReschedInstructorOpen(false)
            setOutdatedSessionModal(true)
          }}
        />
      </CardBox>
    </>
  )
}

export default injectIntl(WithUserTimeHOC(ScheduleListCard))
